import { useEffect, useState } from 'react';
import { useCreditsInfo, useMonthlyUsage, useStorageInfo, useTransactions } from '../features/organizations/hooks';
import { useUser } from '../features/auth/useUser';
import { usePollCreditRefreshAfterPurchase } from '../hooks/usePollCreditRefreshAfterPurchase';
import { clearPurchaseMarker } from '../features/payments/purchaseMarker';
import { PurchasePointsModal } from '../components/payments/PurchasePointsModal';
import { ExpiryWarningBanner } from '../components/credits/ExpiryWarningBanner';
import { GenerateButton } from '../components/generation/common/GenerateButton';
import { brandConfig } from '../config/brands';
import { formatDateTime as formatDate } from '../utils/formatters';
import '../styles/buttons.css';
import '../components/common/CommonTable.css';

export default function DashboardPage() {
  const creditsQuery = useCreditsInfo();
  const usageQuery = useMonthlyUsage();
  const storageQuery = useStorageInfo();
  const { isAdmin } = useUser();
  const { phase: creditRefreshPhase, start: startCreditRefresh, startFromMarker, reset: resetCreditRefresh } =
    usePollCreditRefreshAfterPurchase();
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  // dashboardPointsOnly (usodeki 等) は使用量・ストレージ統計を出さず、ポイント残高のみ表示する。
  const pointsOnly = brandConfig.features.dashboardPointsOnly ?? false;

  // 経路 B: 3DS リダイレクト等でページ遷移をまたいだ場合、復帰時にマーカーを拾って
  // ポーリングを起動する (フルリロード不要で残高反映)。
  useEffect(() => {
    startFromMarker();
  }, [startFromMarker]);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('');
  const transactionsQuery = useTransactions({
    page: currentPage,
    per_page: 10,
    days: 70,
    transaction_type: transactionTypeFilter || undefined,
  } as any);

  if (creditsQuery.isLoading || usageQuery.isLoading || storageQuery.isLoading) {
    return (
      <div className="page admin-page">
        <div className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">
            <img src="/analytics_board_graph_line.svg" alt="" className="page-title-icon" />
            利用状況
          </h1>
          </div>
        </div>
        <div className="admin-loading">読み込み中...</div>
      </div>
    );
  }

  if (creditsQuery.isError || usageQuery.isError || storageQuery.isError) {
    return (
      <div className="page admin-page">
        <div className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">
            <img src="/analytics_board_graph_line.svg" alt="" className="page-title-icon" />
            利用状況
          </h1>
          </div>
        </div>
        <div className="admin-error">
          データの取得に失敗しました
        </div>
      </div>
    );
  }

  const credits = creditsQuery.data;
  const usage = usageQuery.data;
  const storage = storageQuery.data;

  const getCurrentYearMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    return `${year}年${month}月`;
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      purchase: '購入',
      consumption: '消費',
      refund: '返金',
      grant: '付与',
    };
    return labels[type] || type;
  };

  // バイトをMBに変換（整数、カンマ区切り）
  const formatBytesToMB = (bytes: number) => {
    const mb = Math.round(bytes / (1024 * 1024));
    return mb.toLocaleString();
  };

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/analytics_board_graph_line.svg" alt="" className="page-title-icon" />
            利用状況
          </h1>
        </div>
      </div>

      {/* 失効警告バナー */}
      <ExpiryWarningBanner />

      {/* ポイント反映遅延の注意 (Webhook 遅延・失敗時のみ) */}
      {creditRefreshPhase === 'timeout' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            margin: '0 0 16px',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 183, 77, 0.4)',
            backgroundColor: 'rgba(255, 183, 77, 0.08)',
            color: 'var(--dash-ink)',
            fontSize: '13px',
          }}
          role="status"
        >
          <span>
            ポイントの反映に時間がかかっています。しばらくしてから画面を再読み込みしてご確認ください。
          </span>
          <button
            type="button"
            onClick={resetCreditRefresh}
            aria-label="閉じる"
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: 'none',
              color: 'var(--dash-ink-muted)',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div className={`admin-grid-dashboard${pointsOnly ? ' admin-grid-dashboard--single' : ''}`}>
        {/* ポイント残高カード (25%) */}
        <div className="admin-section-card">
          <div className="admin-section-card-header">
            <img src="/money_wallet_open.svg" alt="" className="admin-section-card-header-icon" />
            <span>ポイント残高</span>
          </div>
          <div className="admin-section-card-content" style={{ flexDirection: 'column', gap: '12px', justifyContent: 'flex-start', padding: '16px' }}>
            {/* 期限付きポイント */}
            {(credits?.expirable_credits ?? 0) > 0 && (
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="admin-stat-label" style={{ fontSize: '12px' }}>期限付きポイント</span>
                  <span style={{ fontWeight: 600, color: 'var(--dash-ink-strong)' }}>
                    {Math.floor(credits?.expirable_credits || 0).toLocaleString()} pt
                  </span>
                </div>
                {credits?.expiring_soon && (
                  <div style={{
                    marginTop: '4px',
                    paddingLeft: '12px',
                    fontSize: '11px',
                    color: credits.expiring_soon.days_until_expiry <= 3 ? '#e57373' : '#ffb74d'
                  }}>
                    └ {new Date(credits.expiring_soon.expires_at).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}までに失効 {Math.floor(credits.expiring_soon.amount).toLocaleString()} pt
                  </div>
                )}
              </div>
            )}
            {/* 無期限ポイント */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span className="admin-stat-label" style={{ fontSize: '12px' }}>ポイント</span>
              <span style={{ fontWeight: 600, color: 'var(--dash-ink-strong)' }}>
                {Math.floor(credits?.permanent_credits ?? credits?.credits_balance ?? 0).toLocaleString()} pt
              </span>
            </div>
            {/* 区切り線 */}
            <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(var(--brand-primary-rgb) / 0.3)', margin: '4px 0' }} />
            {/* 合計 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span className="admin-stat-label" style={{ fontSize: '12px', fontWeight: 600 }}>合計</span>
              <span className="admin-stat-value" style={{ fontSize: '18px' }}>
                {Math.floor(credits?.total_available ?? credits?.credits_balance ?? 0).toLocaleString()} pt
              </span>
            </div>
            <GenerateButton
              label={isAdmin ? 'ポイントを購入' : '管理者のみチャージ可能'}
              onClick={() => isAdmin && setIsPurchaseModalOpen(true)}
              disabled={!isAdmin}
              style={{ marginTop: '8px', padding: '10px 20px' }}
              title={!isAdmin ? '管理者のみチャージ可能' : undefined}
            />
          </div>
        </div>

        {/* 今月の使用量サマリー (50%) — usodeki では非表示 */}
        {!pointsOnly && (
        <div className="admin-section-card">
          <div className="admin-section-card-header admin-section-card-header-with-aside">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src="/analytics_board_graph_line.svg" alt="" className="admin-section-card-header-icon" />
              <span>今月の使用量</span>
            </div>
            <span className="admin-section-card-header-aside">{getCurrentYearMonth()}</span>
          </div>
          <div className="admin-section-card-content">
            <div className="admin-usage-stats">
              <div className="admin-usage-stat-item">
                <img src="/picture_stack_landscape.svg" alt="" className="admin-usage-stat-icon" />
                <div className="admin-usage-stat-content">
                  <div className="admin-stat-label">画像生成</div>
                  <div className="admin-stat-value">
                    {usage?.summary?.image_generation_count || 0}
                    <span className="admin-stat-unit">枚</span>
                  </div>
                </div>
              </div>
              <div className="admin-usage-stat-item">
                <img src="/video_player_movie.svg" alt="" className="admin-usage-stat-icon" />
                <div className="admin-usage-stat-content">
                  <div className="admin-stat-label">動画生成</div>
                  <div className="admin-stat-value">
                    {Math.floor(usage?.summary?.video_generation_seconds || 0).toLocaleString()}
                    <span className="admin-stat-unit">秒</span>
                  </div>
                </div>
              </div>
              <div className="admin-usage-stat-item">
                <img src="/cash_payment_bill.svg" alt="" className="admin-usage-stat-icon" />
                <div className="admin-usage-stat-content">
                  <div className="admin-stat-label">消費ポイント</div>
                  <div className="admin-stat-value">
                    {Math.floor(usage?.summary?.total_credits || 0).toLocaleString()}
                    <span className="admin-stat-unit">pt</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        )}

        {/* ストレージ容量カード (25%) — usodeki では非表示 */}
        {!pointsOnly && (
        <div className="admin-section-card">
          <div className="admin-section-card-header">
            <img src="/database_2.svg" alt="" className="admin-section-card-header-icon" />
            <span>ストレージ容量</span>
          </div>
          <div className="admin-section-card-content" style={{ flexDirection: 'column', gap: '16px', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <span className="admin-stat-value" style={{ fontSize: '20px' }}>
                {formatBytesToMB(storage?.storage_used_bytes || 0)}
                <span className="admin-stat-unit">MB</span>
              </span>
              <span className="admin-stat-label" style={{ margin: '0 4px' }}>/</span>
              <span className="admin-stat-label">
                {formatBytesToMB(storage?.max_storage_bytes || 536870912)}
                <span className="admin-stat-label" style={{ marginLeft: '4px' }}>MB</span>
              </span>
            </div>
            <div style={{ width: '100%', padding: '0 16px' }}>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: 'rgba(var(--brand-primary-rgb) / 0.2)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${Math.min(storage?.usage_percentage || 0, 100)}%`,
                  height: '100%',
                  backgroundColor: (storage?.usage_percentage || 0) >= 90 ? '#e57373' : (storage?.usage_percentage || 0) >= 70 ? '#ffb74d' : 'var(--brand-primary)',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{
                textAlign: 'center',
                marginTop: '8px',
                fontSize: '12px',
                color: (storage?.usage_percentage || 0) >= 90 ? '#e57373' : 'var(--dash-ink-muted)',
              }}>
                {Math.round(storage?.usage_percentage || 0)}% 使用中
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* 最近のポイント取引履歴 */}
      <div className="admin-section" style={{ marginTop: '32px' }}>
        <div className="admin-section-header">
          <h2 className="admin-section-title">最近の取引履歴</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <select
                value={transactionTypeFilter}
                onChange={(e) => {
                  setTransactionTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-select"
                style={{
                  padding: '6px 32px 6px 12px',
                  fontSize: '13px',
                }}
              >
                <option value="">すべての種別</option>
                <option value="purchase">購入</option>
                <option value="consumption">消費</option>
                <option value="grant">付与</option>
                <option value="refund">返金</option>
              </select>
              <span style={{ fontSize: '12px', color: 'var(--dash-ink-muted)' }}>
                過去70日間
              </span>
            </div>
          </div>
          {transactionsQuery.isLoading ? (
            <div className="admin-loading">読み込み中...</div>
          ) : transactionsQuery.data && transactionsQuery.data.transactions.length > 0 ? (
            <>
              <div className="table-container">
                <table className="common-table transactions-table">
                  <thead>
                    <tr>
                      <th className="col-date">日時</th>
                      <th className="col-type">種別</th>
                      <th className="col-description">説明</th>
                      <th className="col-credit-type">ポイント種別</th>
                      <th className="col-amount">金額</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactionsQuery.data.transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="col-date">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td className="col-type">
                          <span className={`admin-badge admin-badge-${transaction.transaction_type}`}>
                            {getTransactionTypeLabel(transaction.transaction_type)}
                          </span>
                        </td>
                        <td className="col-description">
                          {(transaction.description || '-').replace(/（期限付き）|（期限付き [^）]+まで）/g, '').trim() || '-'}
                        </td>
                        <td className="col-credit-type">
                          {transaction.credit_type === 'expirable' ? '期限付きポイント' : 'ポイント'}
                        </td>
                        <td className="col-amount" style={{
                          color: transaction.amount > 0 ? '#8fd19e' : 'var(--dash-ink)',
                        }}>
                          {transaction.amount > 0 ? '+' : ''}
                          {Math.floor(transaction.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* ページネーション */}
              {transactionsQuery.data.pagination.total_pages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '16px',
                  marginTop: '20px',
                  padding: '12px 0',
                }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={!transactionsQuery.data.pagination.has_prev}
                    style={{
                      padding: '8px 16px',
                      fontSize: '13px',
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(var(--brand-primary-rgb) / 0.4)',
                      borderRadius: '6px',
                      color: transactionsQuery.data.pagination.has_prev ? 'var(--dash-ink)' : 'var(--dash-ink-faint)',
                      cursor: transactionsQuery.data.pagination.has_prev ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    前へ
                  </button>
                  <span style={{
                    fontSize: '13px',
                    color: 'var(--dash-ink)',
                  }}>
                    {transactionsQuery.data.pagination.page} / {transactionsQuery.data.pagination.total_pages}
                    <span style={{ marginLeft: '8px', color: 'var(--dash-ink-muted)' }}>
                      （全{transactionsQuery.data.pagination.total}件）
                    </span>
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(transactionsQuery.data!.pagination.total_pages, p + 1))}
                    disabled={!transactionsQuery.data.pagination.has_next}
                    style={{
                      padding: '8px 16px',
                      fontSize: '13px',
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(var(--brand-primary-rgb) / 0.4)',
                      borderRadius: '6px',
                      color: transactionsQuery.data.pagination.has_next ? 'var(--dash-ink)' : 'var(--dash-ink-faint)',
                      cursor: transactionsQuery.data.pagination.has_next ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    次へ
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="admin-empty-state">取引履歴がありません</div>
          )}
      </div>

      <PurchasePointsModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        // インライン成功 (経路 A): 渡された情報で直接ポーリングを起動する。
        // Webhook 反映を待って残高・取引履歴をフルリロードなしに更新する。
        // リダイレクトしていないので、経路 B 用に書いたマーカーは消しておく。
        onSuccess={(info) => {
          startCreditRefresh({
            baselineBalance: info.baselineBalance,
            expectedPoints: info.expectedPoints,
            key: info.paymentIntentId,
          });
          clearPurchaseMarker();
        }}
      />
    </div>
  );
}
